import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "../styles/AnnouncementSystem.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";
const EMPTY_RUNTIME = {
  maintenanceModeEnabled: false,
  maintenanceMessage: "",
  maintenanceDurationMinutes: null,
  maintenanceEndsAt: null,
  nextMaintenanceTransitionAt: null,
  announcements: [],
  seenAnnouncementIds: [],
};

function formatMaintenanceDuration(value) {
  const minutes = Number(value);
  if (!Number.isFinite(minutes) || minutes <= 0) return "";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours} hour${hours === 1 ? "" : "s"}${remainingMinutes ? ` ${remainingMinutes} minute${remainingMinutes === 1 ? "" : "s"}` : ""}`;
}

function formatDate(value) {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return year && month && day ? `${year}-${month}-${day}` : value;
}

function typeLabel(type) {
  return type === "maintenance" ? "Maintenance" : type === "update" ? "Update" : "Notice";
}

function AnnouncementContent({ content, compact = false }) {
  const lines = String(content || "").split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  const bullets = lines.length > 0 && lines.every((line) => /^[-*]\s+/.test(line));
  if (bullets) {
    return <ul className={compact ? "announcement-banner-list" : "announcement-entry-list"}>
      {lines.map((line, index) => <li key={index}>{line.replace(/^[-*]\s+/, "")}</li>)}
    </ul>;
  }
  return <div className={compact ? "announcement-banner-copy" : "announcement-entry-copy"}>
    {lines.map((line, index) => <p key={index}>{line}</p>)}
  </div>;
}

export default function AnnouncementSystem({ canBypassMaintenance = false }) {
  const [runtime, setRuntime] = useState(EMPTY_RUNTIME);
  const [open, setOpen] = useState(false);
  const panelRef = useRef(null);
  const bannerRef = useRef(null);
  const maintenanceWasActiveRef = useRef(false);
  const announcements = Array.isArray(runtime.announcements) ? runtime.announcements : [];
  const maintenanceAnnouncements = announcements.filter((item) => item.type === "maintenance");
  const seenIds = useMemo(() => new Set((runtime.seenAnnouncementIds || []).map(Number)), [runtime.seenAnnouncementIds]);
  const unreadCount = announcements.filter((item) => !seenIds.has(Number(item.id))).length;

  useEffect(() => {
    let active = true;
    let timer;
    async function load() {
      try {
        const { data } = await axios.get(`${API_BASE}/api/announcements/runtime`, {
          params: { statusAt: Date.now() },
        });
        if (active) {
          const nextRuntime = { ...EMPTY_RUNTIME, ...data };
          if (maintenanceWasActiveRef.current && !nextRuntime.maintenanceModeEnabled && !canBypassMaintenance) {
            localStorage.clear();
            sessionStorage.clear();
            window.location.replace(`/?refresh=${Date.now()}`);
            return;
          }
          if (nextRuntime.maintenanceModeEnabled) maintenanceWasActiveRef.current = true;
          setRuntime(nextRuntime);
        }
      } catch (error) {
        if (active && error.response?.status !== 401 && error.response?.status !== 403) {
          console.error("Unable to load system announcements:", error);
        }
      }
    }
    const refresh = () => load();
    load();
    timer = window.setInterval(load, 10000);
    window.addEventListener("focus", refresh);
    window.addEventListener("announcements-changed", refresh);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("announcements-changed", refresh);
    };
  }, [canBypassMaintenance]);

  useEffect(() => {
    const transitionAt = runtime.nextMaintenanceTransitionAt;
    if (!transitionAt) return undefined;
    const delay = new Date(transitionAt).getTime() - Date.now() + 300;
    if (!Number.isFinite(delay) || delay <= 0 || delay > 2147000000) return undefined;
    const timer = window.setTimeout(() => window.dispatchEvent(new Event("announcements-changed")), delay);
    return () => window.clearTimeout(timer);
  }, [runtime.nextMaintenanceTransitionAt]);

  useEffect(() => {
    const root = document.documentElement;
    const update = () => root.style.setProperty(
      "--announcement-banner-height",
      `${bannerRef.current?.offsetHeight || 0}px`,
    );
    update();
    const observer = new ResizeObserver(update);
    if (bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener("resize", update);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      root.style.setProperty("--announcement-banner-height", "0px");
    };
  }, [maintenanceAnnouncements.length]);

  useEffect(() => {
    if (!open) return undefined;
    const closeOutside = (event) => {
      if (!panelRef.current?.contains(event.target)) setOpen(false);
    };
    const closeEscape = (event) => event.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("keydown", closeEscape);
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("keydown", closeEscape);
    };
  }, [open]);

  async function togglePanel() {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (!nextOpen || !announcements.length) return;
    const announcementIds = announcements.map((item) => Number(item.id));
    setRuntime((current) => ({ ...current, seenAnnouncementIds: announcementIds }));
    try {
      const { data } = await axios.post(`${API_BASE}/api/announcements/runtime/seen`, { announcementIds });
      setRuntime((current) => ({ ...current, seenAnnouncementIds: data.seenAnnouncementIds || announcementIds }));
    } catch {
      // The optimistic state keeps the badge from distracting the user if the receipt request fails.
    }
  }

  return (
    <>
      {maintenanceAnnouncements.length > 0 && (
        <section className="announcement-system-banner" ref={bannerRef} aria-label="Maintenance announcements">
          {maintenanceAnnouncements.map((item) => (
            <article key={item.id}>
              <div className="announcement-banner-heading">
                <strong>{formatDate(item.announcementDate)}</strong>
                <span className="announcement-type maintenance">Maintenance</span>
                <b>{item.title}</b>
              </div>
              <AnnouncementContent content={item.content} compact />
            </article>
          ))}
        </section>
      )}

      <button
        type="button"
        data-no-standard-button="true"
        className={`announcement-fab${open ? " open" : ""}${unreadCount ? " unread" : ""}`}
        onClick={togglePanel}
        aria-label="Open system information"
        aria-expanded={open}
        title="System information"
      >
        <i className="fas fa-info" aria-hidden="true" />
        {unreadCount > 0 && <span>{unreadCount > 99 ? "99+" : unreadCount}</span>}
      </button>

      {open && (
        <aside className="announcement-popover" ref={panelRef} role="dialog" aria-modal="false" aria-labelledby="system-information-title">
          <header>
            <div>
              <small>System Information</small>
              <h2 id="system-information-title">Updates &amp; Maintenance</h2>
            </div>
            <button type="button" data-no-standard-button="true" onClick={() => setOpen(false)} aria-label="Close system information">&times;</button>
          </header>
          <div className="announcement-popover-body">
            {announcements.length === 0 ? <p className="announcement-empty">No active announcements right now.</p> : announcements.map((item) => (
              <article className="announcement-entry" key={item.id}>
                <div className="announcement-entry-heading">
                  <strong>{formatDate(item.announcementDate)}</strong>
                  <span className={`announcement-type ${item.type}`}>{typeLabel(item.type)}</span>
                </div>
                <h3>{item.title}</h3>
                <AnnouncementContent content={item.content} />
              </article>
            ))}
          </div>
        </aside>
      )}

      {runtime.maintenanceModeEnabled && !canBypassMaintenance && (
        <div className="maintenance-mode-screen" role="alert">
          <div className="maintenance-mode-card">
            <span>Maintenance</span>
            <div className="maintenance-mode-icon" aria-hidden="true">
              <i className="fas fa-tools" />
            </div>
            <h1>System Maintenance in Progress</h1>
            <p>{runtime.maintenanceMessage || "The system is currently under maintenance and will be back shortly. Thank you for your patience."}</p>
            {formatMaintenanceDuration(runtime.maintenanceDurationMinutes) && (
              <p className="maintenance-mode-duration"><i className="far fa-clock" aria-hidden="true" /> Expected duration: <strong>{formatMaintenanceDuration(runtime.maintenanceDurationMinutes)}</strong></p>
            )}
          </div>
        </div>
      )}
    </>
  );
}
